import PropTypes from 'prop-types';
import React, { createContext, useMemo, useState, useCallback } from 'react';
import { PreferredLocale } from '../../constants/cookies';
import { ChildrenPropType } from '../../prop-types/children';
import { getSupportedLocale } from './get-supported-locale';

import { initialLocaleState } from './initial-state';
import { GetBrowserLocale } from './get-browser-locale';
import { Development } from './locale-codes';
import { useCookie } from '../../hooks/use-cookie-value';

const LocaleContext = createContext();

const LocaleContextProvider = ({ children, availableLocales, priorityLocale = null }) => {
  const [locale, setLocale] = useState(initialLocaleState);
  const { cookieValue, setCookieValue } = useCookie(PreferredLocale);
  const browserDisplayLocale = GetBrowserLocale();

  const updateLocale = useCallback(
    (updatedLocaleValue) => {
      if (availableLocales.includes(updatedLocaleValue)) {
        setLocale(updatedLocaleValue);
        if (updatedLocaleValue === Development) {
          // Do not cache or save the development locale as a user setting
          return;
        }

        setCookieValue(updatedLocaleValue);
      }
    },
    [availableLocales, setCookieValue, setLocale],
  );

  const value = useMemo(() => {
    const localeToUse =
      (locale === Development && getSupportedLocale(availableLocales, locale)) ||
      (cookieValue && getSupportedLocale(availableLocales, cookieValue)) ||
      (priorityLocale && getSupportedLocale(availableLocales, priorityLocale)) ||
      (browserDisplayLocale && getSupportedLocale(availableLocales, browserDisplayLocale)) ||
      locale;

    return {
      locale: localeToUse,
      setLocale: updateLocale,
      availableLocales,
    };
  }, [availableLocales, browserDisplayLocale, cookieValue, locale, updateLocale, priorityLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

LocaleContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  availableLocales: PropTypes.arrayOf(PropTypes.string).isRequired,
  priorityLocale: PropTypes.string,
};

export { LocaleContext, LocaleContextProvider };
